import { Link } from "react-router-dom";
import { ArrowRight, Shield, FileText, Palette, Award } from "lucide-react";

const trademarks = [
  { name: "RLK", regNumber: "UK00004012281", classes: "9, 12", status: "Registered" },
  { name: "RLK (Figurative)", regNumber: "UK00004038572", classes: "7, 9, 12, 13, 37, 42, 45", status: "Registered" },
  { name: "AI-AV", regNumber: "UK00004047302", classes: "7, 9, 12, 13, 39, 41, 45", status: "Registered" },
  { name: "Helais", regNumber: "UK00004201532", classes: "9, 12", status: "Registered" },
  { name: "UrbanAnalytix", regNumber: "UK00004011910", classes: "9", status: "Registered" },
  { name: "PAY-AI", regNumber: "UK00004021731", classes: "9, 36", status: "Registered" },
  { name: "PAY-I", regNumber: "UK00004021736", classes: "9, 36", status: "Registered" },
];

const patents = [
  { 
    number: "GB2507837.9", 
    title: "Helais Autonomous Aerial Support System", 
    filingDate: "20 May 2025",
    wipo: true 
  },
  { 
    number: "GB2507388.3", 
    title: "Vision-Based Target-Oriented Adaptive Control Framework for Autonomous Systems", 
    filingDate: "13 May 2025",
    wipo: true 
  },
  { 
    number: "GB2511230.1", 
    title: "Talon Autonomous Vehicle-Mounted UAV System", 
    filingDate: "27 May 2025",
    wipo: true 
  },
  { 
    number: "GB2511317.6", 
    title: "System and Method for Reinforced Probabilistic Optimisation of Discrete Selections", 
    filingDate: "13 July 2025",
    wipo: true 
  },
  { 
    number: "GB2415465.0", 
    title: "Pay-AI: Comprehensive Automated Fueling System", 
    filingDate: "21 October 2024",
    wipo: true 
  },
];

const designs = [
  { 
    number: "6411603", 
    title: "Unmanned Aerial Vehicles", 
    registrationDate: "16 December 2024",
    grantDate: "20 January 2025"
  },
];

export default function IntellectualPropertyPage() {
  return (
    <div className="relative pt-20">
      {/* Hero */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-fine" />
        <div className="absolute inset-0 noise-texture" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl">
            <p className="text-caption mb-4 opacity-0 animate-fade-up">Legal</p>
            <h1 className="text-display text-foreground mb-6 opacity-0 animate-fade-up delay-100">
              Intellectual Property
            </h1>
            <p className="text-subtitle opacity-0 animate-fade-up delay-200">
              The RLK Group maintains a portfolio of registered trademarks, filed patents, and registered designs protecting our innovations.
            </p>
          </div>
        </div>
      </section>

      {/* Trademarks Section */}
      <section className="section-padding-lg relative" aria-labelledby="trademarks-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center" aria-hidden="true">
                <Award className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-caption">Registered Trademarks</p>
            </div>
            <h2 id="trademarks-heading" className="text-headline text-foreground mb-4">
              Trademarks
            </h2>
            <p className="text-body mb-8">
              The following are registered trademarks of The RLK Group. All rights reserved.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-4 pr-6 text-xs uppercase tracking-wider text-muted-foreground font-medium">Mark</th>
                    <th className="py-4 pr-6 text-xs uppercase tracking-wider text-muted-foreground font-medium">Registration No.</th>
                    <th className="py-4 pr-6 text-xs uppercase tracking-wider text-muted-foreground font-medium">Classes</th>
                    <th className="py-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trademarks.map((tm, index) => (
                    <tr key={index} className="border-b border-border/50">
                      <td className="py-4 pr-6">
                        <span className="text-label text-foreground">{tm.name}</span>
                      </td>
                      <td className="py-4 pr-6">
                        <span className="text-sm font-mono text-muted-foreground">{tm.regNumber}</span>
                      </td>
                      <td className="py-4 pr-6">
                        <span className="text-sm text-muted-foreground">{tm.classes}</span>
                      </td>
                      <td className="py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                          {tm.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="divider" role="separator" />
      </div>

      {/* Patents Section */}
      <section className="section-padding-lg relative bg-card/30 edge-glow" aria-labelledby="patents-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center" aria-hidden="true">
                <FileText className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-caption">Patent Portfolio</p>
            </div>
            <h2 id="patents-heading" className="text-headline text-foreground mb-4">
              Patents
            </h2>
            <p className="text-body mb-8">
              Filed patent applications protecting The RLK Group&apos;s innovations. All patents are filed with WIPO designation.
            </p>

            <div className="space-y-4">
              {patents.map((patent, index) => (
                <article key={index} className="card-platform p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-label text-foreground mb-2">{patent.title}</h3>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <span>
                          <span className="text-muted-foreground/60">Patent No:</span>{" "}
                          <span className="font-mono">{patent.number}</span>
                        </span>
                        <span>
                          <span className="text-muted-foreground/60">Filed:</span>{" "}
                          {patent.filingDate}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        Filed
                      </span>
                      {patent.wipo && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-muted text-muted-foreground border border-border">
                          WIPO
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="divider" role="separator" />
      </div>

      {/* Designs Section */}
      <section className="section-padding-lg relative" aria-labelledby="designs-heading">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center" aria-hidden="true">
                <Palette className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-caption">Registered Designs</p>
            </div>
            <h2 id="designs-heading" className="text-headline text-foreground mb-4">
              Designs
            </h2>
            <p className="text-body mb-8">
              Registered designs protecting the visual appearance of The RLK Group&apos;s products.
            </p>

            <div className="space-y-4">
              {designs.map((design, index) => (
                <article key={index} className="card-platform p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-label text-foreground mb-2">{design.title}</h3>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                        <span>
                          <span className="text-muted-foreground/60">Design No:</span>{" "}
                          <span className="font-mono">{design.number}</span>
                        </span>
                        <span>
                          <span className="text-muted-foreground/60">Registered:</span>{" "}
                          {design.registrationDate}
                        </span>
                        <span>
                          <span className="text-muted-foreground/60">Granted:</span>{" "}
                          {design.grantDate}
                        </span>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                      Granted
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Legal Notice */}
      <section className="section-padding relative bg-card/30 edge-glow" aria-labelledby="legal-notice">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center" aria-hidden="true">
                <Shield className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-caption">Legal Notice</p>
            </div>
            <h2 id="legal-notice" className="text-title text-foreground mb-4">
              Protection of Rights
            </h2>
            <p className="text-body mb-4">
              All trademarks, patents, and registered designs listed on this page are the property of The RLK Group. Unauthorised use, reproduction, or infringement of these intellectual property rights is strictly prohibited and may result in legal action.
            </p>
            <p className="text-sm text-muted-foreground">
              For licensing enquiries or to report potential infringement, please contact us.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg relative" aria-labelledby="ip-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 max-w-4xl mx-auto">
            <div>
              <h2 id="ip-cta" className="text-title text-foreground mb-3">Licensing Enquiries</h2>
              <p className="text-body">For intellectual property or licensing discussions.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/about" className="btn-secondary">
                About Us
              </Link>
              <Link to="/contact" className="btn-primary">
                Contact Us
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
